import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FieldInput } from "@/components/FieldInput";
import { ReportPanel } from "@/components/ReportPanel";
import { EntryDialog } from "@/components/EntryDialog";
import { ENTRY_KINDS } from "@/lib/entries";
import { api, can, errMsg, inr, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";

const ACTIONS = ["party-receivable", "party-payable", "party-received", "party-paid"];
const FIELDS = [
  { name: "name", label: "Party Name", type: "text", required: true },
  { name: "mobile", label: "Mobile Number", type: "text" },
  { name: "distributor_id", label: "Distributor Account", type: "distributor", required: true },
  { name: "balance_type", label: "Balance Type", type: "select", required: true,
    options: [["Receivable", "Receivable (Humein Payment Leni Hai)"], ["Payable", "Payable (Humein Payment Deni Hai)"]] },
  { name: "opening", label: "Opening Balance (₹)", type: "money", hint: "Sirf Admin set kar sakta hai — bank/cash balance change nahi hota" },
  { name: "entry_date", label: "Entry Date", type: "date", required: true },
  { name: "status", label: "Status", type: "select", options: ["Active", "Inactive"], required: true },
  { name: "remarks", label: "Remarks", type: "textarea" },
];

function PartyDialog({ item, masters, onClose }) {
  const [v, setV] = useState({});
  useEffect(() => {
    setV(item ? { name: item.party_name, mobile: item.mobile, distributor_id: item.distributor_id, balance_type: item.balance_type,
      opening: String(item.opening_balance), entry_date: item.entry_date, status: item.status, remarks: item.remarks }
      : { distributor_id: "AYZAL", balance_type: "Receivable", opening: "0", entry_date: todayISO(), status: "Active" });
  }, [item]);
  const save = async () => {
    if (!v.name) return toast.error("Party name is required");
    try {
      if (item) await api.put(`/masters/party/${item.party_id}`, v);
      else await api.post("/masters/party", v);
      toast.success("Party saved");
      masters.reload();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="party-dialog">
        <DialogHeader><DialogTitle className="font-display">{item ? `Edit Party — ${item.party_id}` : "Add Party"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.name} className={f.hint || f.type === "textarea" ? "sm:col-span-2" : ""}>
              <FieldInput f={f} value={v[f.name]} onChange={(x) => setV((s) => ({ ...s, [f.name]: x }))} masters={masters} values={v} testPrefix="party" />
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} data-testid="party-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartyTable({ rows, canWrite, onEdit }) {
  return (
    <div className="overflow-auto border border-slate-200 rounded-xl bg-white">
      <table className="dense-table" data-testid="party-master-table">
        <thead><tr><th>ID</th><th>Party</th><th>Mobile</th><th>Distributor</th><th>Balance Type</th><th className="text-right">Opening</th><th>Entry Date</th><th>Status</th>{canWrite && <th />}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 py-8">Koi party nahi — "Add Party" se shuru karein</td></tr>}
          {rows.map((r) => (
            <tr key={r.party_id} data-testid={`party-row-${r.party_id}`}>
              <td className="num font-semibold">{r.party_id}</td><td>{r.party_name}</td><td className="num">{r.mobile}</td><td>{r.distributor_id}</td>
              <td><span className={`text-[11px] px-1.5 py-0.5 rounded border ${r.balance_type === "Receivable" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>{r.balance_type}</span></td>
              <td className="num text-right">{inr(r.opening_balance)}</td><td className="num">{r.entry_date}</td><td>{r.status}</td>
              {canWrite && <td><button onClick={() => onEdit(r)} data-testid={`edit-party-${r.party_id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Parties() {
  const { user } = useAuth();
  const masters = useMasters();
  const [kind, setKind] = useState(null);
  const [edit, setEdit] = useState(undefined);
  const canWrite = can(user, "master:write");
  const actions = ACTIONS.filter((k) => can(user, ENTRY_KINDS[k].perm));
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="h-1 w-14 rounded-full mb-3 bg-[#0F766E]" />
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" data-testid="parties-page-title">Creditors & Debtors</h1>
          <span className="hint-tag mt-2">Receivable = humein lena hai · Payable = humein dena hai · Net = Receivable − Payable</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite && <Button size="sm" variant="outline" onClick={() => setEdit(null)} data-testid="add-party-btn"><Plus className="h-4 w-4" /> Add Party</Button>}
          {actions.map((k) => (
            <Button key={k} size="sm" onClick={() => setKind(k)} className="bg-[#0F766E] text-white hover:opacity-90" data-testid={`party-action-${k}`}>
              <Plus className="h-4 w-4" /> {ENTRY_KINDS[k].title}
            </Button>
          ))}
        </div>
      </div>
      <PartyTable rows={masters.party} canWrite={canWrite} onEdit={setEdit} />
      <ReportPanel reportKey="party-ledger" filters={["distributor_id", "party_id", "transaction_type"]} />
      {kind && <EntryDialog kind={kind} open={!!kind} onOpenChange={(o) => !o && setKind(null)} />}
      {edit !== undefined && <PartyDialog item={edit} masters={masters} onClose={() => setEdit(undefined)} />}
    </div>
  );
}
